'use client';

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

import { cn } from '../../utils/cn/cn';
import { Label } from '../Label/Label';
import { Notification } from '../Notification/Notification';

export interface RadioProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  label?: string;
  error?: boolean;
}
export interface RadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {
  error?: boolean;
  helperText?: string;
}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ error, helperText, className, children, ...props }, ref) => (
  <RadioGroupPrimitive.Root className={cn('grid gap-2', className)} {...props} ref={ref}>
    {children}
    {helperText && (
      <Notification severity={error ? 'error' : 'info'} variant="feedback" className="mt-0.5">
        {helperText}
      </Notification>
    )}
  </RadioGroupPrimitive.Root>
));
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioProps
>(({ id, label, error, className, disabled, ...props }, ref) => {
  const fallBackId = React.useId();
  return (
    <div className="flex items-center gap-1">
      <RadioGroupPrimitive.Item
        ref={ref}
        id={id || fallBackId}
        className={cn(
          'border-color-text-dark aspect-square h-2 w-2 rounded-full border focus:outline-none disabled:opacity-35',
          error && `border-color-error border-2`,
          className,
        )}
        disabled={disabled}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <div className="bg-color-text-dark h-1 w-1 rounded-full" />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      {label && (
        <Label
          htmlFor={id || fallBackId}
          className={`text-size-body2 ${disabled ? 'opacity-35' : ''}`}
        >
          {label}
        </Label>
      )}
    </div>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
